import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, FileText, Images } from 'lucide-react'
import { Button } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'

export interface MedienUebersicht { id: number; fotos: number; ir: number; ids: number[] }

export async function dateiLaden(pfad: string, params: Record<string, any> | undefined, name: string, mime: string) {
  const r = await api.get(pfad, { params, responseType: 'blob', timeout: 0 })
  const url = URL.createObjectURL(new Blob([r.data], { type: mime }))
  const a = document.createElement('a')
  a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

const sauber = (s: string) => s.replace(/[^\w.\- äöüÄÖÜß]+/g, '_').trim()

function useAktion() {
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const start = async (key: string, f: () => Promise<void>, text: string) => {
    setLaeuft(key); setFehler(null)
    try { await f() } catch { setFehler(text) } finally { setLaeuft(null) }
  }
  return { laeuft, fehler, start }
}

/** Reiter „Medien“ im Einsatz: Zähler, Galerie öffnen, ZIP laden. */
export function EinsatzMedien({ einsatzId, titel, medien, laedt }: { einsatzId: number; titel: string; medien: MedienUebersicht | null; laedt: boolean }) {
  const navigate = useNavigate()
  const { laeuft, fehler, start } = useAktion()
  if (laedt && !medien) return <p className="text-sm text-dark-text-muted">Lade Medien…</p>
  const n = medien?.fotos ?? 0
  return (
    <div className="space-y-4">
      <p className="text-sm text-dark-text-muted">
        <span className="font-semibold text-dark-text">{n}</span> Foto{n === 1 ? '' : 's'} zu diesem Einsatz
        {medien && medien.ir > 0 && <> · davon {medien.ir} Infrarot</>}.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate(`/medien?gruppe=${einsatzId}`)} disabled={n === 0}><Images className="mr-1.5 h-4 w-4" />In Galerie öffnen</Button>
        <Button variant="secondary" disabled={n === 0 || !!laeuft}
          onClick={() => start('zip', () => dateiLaden('/dji/medien/galerie/zip', { ids: (medien?.ids ?? []).join(',') }, `fotos_einsatz-${einsatzId}.zip`, 'application/zip'), 'ZIP konnte nicht erstellt werden.')}>
          <Download className="mr-1.5 h-4 w-4" />{laeuft === 'zip' ? 'Wird erstellt…' : 'Alle Fotos als ZIP'}
        </Button>
      </div>
      {n === 0 && <p className="text-xs text-dark-text-subtle">Zu „{titel}“ liegen noch keine Fotos aus DJI Pilot 2 vor.</p>}
      {fehler && <p className="text-sm text-red-300">{fehler}</p>}
    </div>
  )
}

/** Reiter „Bericht & Export“: Trupp-Protokoll, Flugberichte je Flug, Medien-ZIP. */
export function EinsatzExport({ einsatzId, titel, nummer, fluege, medien, flugLabel }: {
  einsatzId: number; titel: string; nummer?: string | null
  fluege: { id: number; flug_nr?: string | null; startzeit?: string | null }[]
  medien: MedienUebersicht | null
  flugLabel: (f: any) => string
}) {
  const { laeuft, fehler, start } = useAktion()
  const name = sauber([nummer, titel].filter(Boolean).join(' ') || String(einsatzId))
  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-dark-text">Einsatz</h3>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={!!laeuft}
            onClick={() => start('trupps', () => dateiLaden(`/einsaetze/${einsatzId}/trupps-bericht.pdf`, undefined, `Trupp-Protokoll ${name}.pdf`, 'application/pdf'), 'Das Trupp-Protokoll konnte nicht erstellt werden.')}>
            <FileText className="mr-1.5 h-4 w-4" />{laeuft === 'trupps' ? 'Wird erstellt…' : 'Trupp-Protokoll (PDF)'}
          </Button>
          <Button variant="secondary" disabled={!medien || medien.fotos === 0 || !!laeuft}
            onClick={() => start('zip', () => dateiLaden('/dji/medien/galerie/zip', { ids: (medien?.ids ?? []).join(',') }, `fotos_einsatz-${einsatzId}.zip`, 'application/zip'), 'ZIP konnte nicht erstellt werden.')}>
            <Download className="mr-1.5 h-4 w-4" />{laeuft === 'zip' ? 'Wird erstellt…' : `Medien als ZIP${medien ? ` (${medien.fotos})` : ''}`}
          </Button>
        </div>
      </section>
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-dark-text">Flugberichte ({fluege.length})</h3>
        {fluege.length === 0 ? <p className="text-xs text-dark-text-subtle">Keine Flüge zu diesem Einsatz.</p> : (
          <ul className="divide-y divide-dark-border rounded-xl border border-dark-border">
            {fluege.map(f => (
              <li key={f.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className="mr-auto min-w-0 text-sm text-dark-text">{flugLabel(f)}</span>
                {(['ausfuehrlich', 'kurz'] as const).map(u => (
                  <Button key={u} variant="secondary" disabled={!!laeuft}
                    onClick={() => start(`f${f.id}${u}`, () => dateiLaden(`/fluege/${f.id}/flugbericht`, { umfang: u }, `Flugbericht ${f.flug_nr ?? f.id}${u === 'kurz' ? ' kurz' : ''}.pdf`, 'application/pdf'), 'Der Flugbericht konnte nicht erstellt werden.')}>
                    <FileText className="mr-1.5 h-4 w-4" />{laeuft === `f${f.id}${u}` ? 'Wird erstellt…' : u === 'kurz' ? 'Flugbericht kurz' : 'Flugbericht'}
                  </Button>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>
      {fehler && <p className="text-sm text-red-300">{fehler}</p>}
    </div>
  )
}
